import React, { useState } from 'react';
import { Button, StatusBadge, Typography, BtnIcon } from '@growthaxl/web-foundation';

interface PlanStep {
  id: string;
  title: string;
  status?: 'pending' | 'running' | 'completed' | 'failed';
}

export interface PlanCardProps {
  title: string;
  steps: PlanStep[];
  onApprove?: () => void;
  onReject?: () => void;
  approved?: boolean;
  className?: string;
}

const STATUS_BADGE_VARIANT: Record<string, 'neutral' | 'info' | 'success' | 'error'> = {
  pending: 'neutral',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const STATUS_ICON: Record<string, 'check' | 'close' | 'play' | undefined> = {
  completed: 'check',
  failed: 'close',
  running: 'play',
};

export function PlanCard({
  title,
  steps,
  onApprove,
  onReject,
  approved,
  className,
}: PlanCardProps): React.ReactElement {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={`border border-border-default rounded-8 overflow-hidden text-body-sm ${className ?? ''}`}>
      <button
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        className="w-full px-3 py-2.5 flex justify-between items-center bg-canvas border-none cursor-pointer text-left"
      >
        <Typography variant="subtitle2">{title}</Typography>
        <span className="text-caption">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-3 py-2 flex flex-col gap-1.5">
          {steps.map((step, i) => (
            <div key={step.id} className="flex items-center gap-2">
              <Typography variant="caption" color="secondary">
                <span className="inline-block w-5 text-right shrink-0">{i + 1}.</span>
              </Typography>
              <Typography variant="body2" color="primary">
                <span className="flex-1">{step.title}</span>
              </Typography>
              {step.status && (
                <StatusBadge
                  variant={STATUS_BADGE_VARIANT[step.status] ?? 'neutral'}
                  size="sm"
                  styleMode="soft"
                >
                  {STATUS_ICON[step.status] && (
                    <BtnIcon name={STATUS_ICON[step.status]!} size={10} />
                  )}
                  {step.status}
                </StatusBadge>
              )}
            </div>
          ))}

          {(onApprove || onReject) && !approved && (
            <div className="flex gap-2 mt-2">
              {onApprove && (
                <Button variant="primary" size="xs" onClick={onApprove}>
                  Approve
                </Button>
              )}
              {onReject && (
                <Button variant="danger" size="xs" onClick={onReject}>
                  Reject
                </Button>
              )}
            </div>
          )}
          {approved && (
            <Typography variant="caption" color="primary">
              <p className="text-success-strong mt-1">Plan approved</p>
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}
